import type { InferRpcDefinition } from "@redotech/rpc/definition";
import { canExecuteCheckoutSplitAgentForTeam } from "./schema/ab-tests/can-execute-checkout-split-agent-for-team";
import { canStartCheckoutAbTest } from "./schema/ab-tests/can-start-checkout-ab-test";
import { concludeCheckoutAbTest } from "./schema/ab-tests/conclude-checkout-ab-test";
import { createCheckoutSplitFromBuilder } from "./schema/ab-tests/create-checkout-split-from-builder";
import { createCheckoutSplitFromFlowSteps } from "./schema/ab-tests/create-checkout-split-from-flow-steps";
import { createCheckoutSplitGeneration } from "./schema/ab-tests/create-checkout-split-generation";
import { createProspectiveCheckoutSplitFromFlowSteps } from "./schema/ab-tests/create-prospective-checkout-split-from-flow-steps";
import { deleteScheduledAbTest } from "./schema/ab-tests/delete-scheduled-ab-test";
import { executeCheckoutSplitAgent } from "./schema/ab-tests/execute-checkout-split-agent";
import { getCheckoutSplitDetails } from "./schema/ab-tests/get-checkout-split-details";
import { getCheckoutSplitListMetrics } from "./schema/ab-tests/get-checkout-split-list-metrics";
import { getConcludedAndActiveCheckoutSplits } from "./schema/ab-tests/get-concluded-and-active-checkout-splits";
import { getScheduledSplitTrees } from "./schema/ab-tests/get-scheduled-split-trees";
import { getSplitLibrarySplits } from "./schema/ab-tests/get-split-library-splits";
import { getUpcomingCheckoutSplitsInOrder } from "./schema/ab-tests/get-upcoming-checkout-splits-in-order";
import { setSplitAnalyticsVersionOverride } from "./schema/ab-tests/set-split-analytics-version-override";
import { setUpcomingCheckoutSplitsOrder } from "./schema/ab-tests/set-upcoming-checkout-splits-order";
import { startScheduledCheckoutAbTest } from "./schema/ab-tests/start-scheduled-checkout-ab-test";
import { unsafeDeleteCheckoutAbTest } from "./schema/ab-tests/unsafe-delete-checkout-ab-test";
import { validateCheckoutTreeTransformation } from "./schema/ab-tests/validate-checkout-tree-transformation";
import { validateCheckoutTreeTransformationForNewSplit } from "./schema/ab-tests/validate-checkout-tree-transformation-for-new-split";
import { activateCheckoutTreeById } from "./schema/activate-checkout-tree-by-id";
import { addStorefrontDeliveryEstimateSelector } from "./schema/add-storefront-delivery-estimate-selector";
import { calculateCheckoutSplitResults } from "./schema/analytics/calculate-checkout-split-results";
import { getAllCheckoutSplitMetrics } from "./schema/analytics/get-all-checkout-split-metrics";
import { getCheckoutOptimizationAnalyticsAssumptions } from "./schema/analytics/get-checkout-optimization-analytics-assumptions";
import { getCheckoutOptimizationRevenuePerCheckoutExperienceMetrics } from "./schema/analytics/get-checkout-optimization-revenue-per-checkout-experience-metrics";
import { getCheckoutOptimizationSummaryMetrics } from "./schema/analytics/get-checkout-optimization-summary-metrics";
import { getCheckoutOptimizationTimeSeriesMetrics } from "./schema/analytics/get-checkout-optimization-time-series-metrics";
import { getCheckoutProbabilityMeasurementMetrics } from "./schema/analytics/get-checkout-probability-measurement-metrics";
import { getCheckoutSplitAiAnalysis } from "./schema/analytics/get-checkout-split-ai-analysis";
import { getCheckoutSplitSummaryMetrics } from "./schema/analytics/get-checkout-split-summary-metrics";
import { getCustomVisualsDataForSplit } from "./schema/analytics/get-custom-visuals-data-for-split";
import { getImpactDashboardMetrics } from "./schema/analytics/get-impact-dashboard-metrics";
import { getMercuryAnalyticsForCheckoutExperience } from "./schema/analytics/get-mercury-analytics-for-checkout-experience";
import { getMercurySummaryAnalytics } from "./schema/analytics/get-mercury-summary-analytics";
import { getOnlineStoreOrderCounts } from "./schema/analytics/get-online-store-order-counts";
import { getOnlineStoreSessions } from "./schema/analytics/get-online-store-sessions";
import { getRecentRescuePanelSplitData } from "./schema/analytics/get-recent-rescue-panel-split-data";
import { getRescuePanelAttribution } from "./schema/analytics/get-rescue-panel-attribution";
import { getSegmentedCheckoutMetrics } from "./schema/analytics/get-segmented-checkout-metrics";
import { getSegmentedCheckoutMetricsForSplit } from "./schema/analytics/get-segmented-checkout-metrics-for-split";
import { getSegmentedCheckoutMetricsInsights } from "./schema/analytics/get-segmented-checkout-metrics-insights";
import { getThresholdUpsellWidgetAnalytics } from "./schema/analytics/get-threshold-upsell-widget-analytics";
import { getWidgetProductAnalytics } from "./schema/analytics/get-widget-product-analytics";
import { getWidgetsSummaryMetrics } from "./schema/analytics/get-widgets-summary-metrics";
import { getWidgetsTimeSeriesMetrics } from "./schema/analytics/get-widgets-time-series-metrics";
import { sendScheduleDemoClickedNotification } from "./schema/analytics/send-schedule-demo-clicked-notification";
import { updateCheckoutOptimizationAnalyticsAssumptions } from "./schema/analytics/update-checkout-optimization-analytics-assumptions";
import { activateIntentRescue } from "./schema/billing/activate-intent-rescue";
import { enableIntentRescueBilling } from "./schema/billing/enable-intent-rescue-billing";
import { generatePdfForInvoice } from "./schema/billing/generate-pdf-for-invoice";
import { getCurrentCheckoutOptimizationInvoice } from "./schema/billing/get-current-checkout-optimization-invoice";
import { getFinalizedServiceInvoices } from "./schema/billing/get-finalized-service-invoices";
import { addUpsellPageToCheckoutExperience } from "./schema/checkout-experiences/add-upsell-page-to-checkout-experience";
import { assignWidgetsToExperience } from "./schema/checkout-experiences/assign-widgets-to-experience";
import { canSetCheckoutExperienceActive } from "./schema/checkout-experiences/can-set-checkout-experience-active";
import { constrainRateTableRelativeToOtherRateTable } from "./schema/checkout-experiences/constrain-rate-table-relative-to-other-rate-table";
import { createCheckoutExperience } from "./schema/checkout-experiences/create-checkout-experience";
import { createCheckoutWidget } from "./schema/checkout-experiences/create-checkout-widget";
import { deleteCheckoutExperience } from "./schema/checkout-experiences/delete-checkout-experience";
import { deleteCheckoutWidget } from "./schema/checkout-experiences/delete-checkout-widget";
import { duplicateCheckoutExperience } from "./schema/checkout-experiences/duplicate-checkout-experience";
import { duplicateCheckoutWidget } from "./schema/checkout-experiences/duplicate-checkout-widget";
import { generateContentBlockWidget } from "./schema/checkout-experiences/generate-content-block-widget";
import { getAllMercuryModelsForCheckoutExperience } from "./schema/checkout-experiences/get-all-mercury-models-for-checkout-experience";
import { getCheckoutExperienceById } from "./schema/checkout-experiences/get-checkout-experience-by-id";
import { getCheckoutExperienceParcelTreeFlowSteps } from "./schema/checkout-experiences/get-checkout-experience-parcel-tree-flow-steps";
import { getCheckoutExperiences } from "./schema/checkout-experiences/get-checkout-experiences";
import { getCheckoutExperiencesForAbTestBuilder } from "./schema/checkout-experiences/get-checkout-experiences-for-ab-test-builder";
import { getCheckoutWidgets } from "./schema/checkout-experiences/get-checkout-widgets";
import { getConfiguredServiceCodes } from "./schema/checkout-experiences/get-configured-service-codes";
import { getRateTableNameAndDescriptionById } from "./schema/checkout-experiences/get-rate-table-name-and-description-by-id";
import { getShippingOptionWidgets } from "./schema/checkout-experiences/get-shipping-option-widgets";
import { migrateCheckoutExperienceToConstraintsV2 } from "./schema/checkout-experiences/migrate-checkout-experience-to-constraints-v2";
import { removeUpsellPageFromCheckoutExperience } from "./schema/checkout-experiences/remove-upsell-page-from-checkout-experience";
import { setCheckoutExperienceActive } from "./schema/checkout-experiences/set-checkout-experience-active";
import { setShippingMethodOrder } from "./schema/checkout-experiences/set-shipping-method-order";
import { updateCheckoutExperience } from "./schema/checkout-experiences/update-checkout-experience";
import { updateCheckoutParcelTree } from "./schema/checkout-experiences/update-checkout-parcel-tree";
import { updateCheckoutWidget } from "./schema/checkout-experiences/update-checkout-widget";
import { updateMercuryModel } from "./schema/checkout-experiences/update-mercury-model";
import { createCheckoutTree } from "./schema/create-checkout-tree";
import { createDiscountRule } from "./schema/discount-rules/create-discount-rule";
import { deleteDiscountRule } from "./schema/discount-rules/delete-discount-rule";
import { getDiscountRule } from "./schema/discount-rules/get-discount-rule";
import { getDiscountRules } from "./schema/discount-rules/get-discount-rules";
import { reorderDiscountRules } from "./schema/discount-rules/reorder-discount-rules";
import { setDiscountRuleActive } from "./schema/discount-rules/set-discount-rule-active";
import { setUsingDiscountRules } from "./schema/discount-rules/set-using-discount-rules";
import { updateDiscountRule } from "./schema/discount-rules/update-discount-rule";
import { startGaleKyc } from "./schema/gale/start-gale-kyc";
import { getCheckoutProbabilityRescueModalSettings } from "./schema/get-checkout-probability-rescue-modal-settings";
import { getCheckoutTreeAnalytics } from "./schema/get-checkout-tree-analytics";
import { getCheckoutTreeFlowSteps } from "./schema/get-checkout-tree-flow-steps";
import { getCheckoutTreeFlowStepsByEventId } from "./schema/get-checkout-tree-flow-steps-by-event-id";
import { getCheckoutTreeFlowStepsById } from "./schema/get-checkout-tree-flow-steps-by-id";
import { getCheckoutTreeFlowStepsForSplit } from "./schema/get-checkout-tree-flow-steps-for-split";
import { getCheckoutTreeRecords } from "./schema/get-checkout-tree-records";
import { getCustomJsSettings } from "./schema/get-custom-js-settings";
import { getFulfillmentCutoffSchedule } from "./schema/get-fulfillment-cutoff-schedule";
import { getOmsShippingMethodNameConsiderations } from "./schema/get-oms-shipping-method-name-considerations";
import { getStorefrontDeliveryEstimateSettings } from "./schema/get-storefront-delivery-estimate-settings";
import { getStorefrontProgressBarSettings } from "./schema/get-storefront-progress-bar-settings";
import { getSubscriptionPreOrderDeliveryProfileSettings } from "./schema/get-subscription-pre-order-delivery-profile-settings";
import { canStartPostPurchaseAbTest } from "./schema/post-purchase/can-start-post-purchase-ab-test";
import { concludePostPurchaseAbTest } from "./schema/post-purchase/conclude-post-purchase-ab-test";
import { createPostPurchaseSplitFromFlowSteps } from "./schema/post-purchase/create-post-purchase-split-from-flow-steps";
import { deleteUpsellPage } from "./schema/post-purchase/delete-upsell-page";
import { duplicateUpsellPage } from "./schema/post-purchase/duplicate-upsell-page";
import { getPostPurchaseFlowSteps } from "./schema/post-purchase/get-post-purchase-flow-steps";
import { getPostPurchaseSplits } from "./schema/post-purchase/get-post-purchase-splits";
import { getPpuAbTestDetail } from "./schema/post-purchase/get-ppu-ab-test-detail";
import { getPpuAbTestMetrics } from "./schema/post-purchase/get-ppu-ab-test-metrics";
import { getPpuAbTestSummaryMetrics } from "./schema/post-purchase/get-ppu-ab-test-summary-metrics";
import { getPpuSplitAiAnalysis } from "./schema/post-purchase/get-ppu-split-ai-analysis";
import { getUpsellPages } from "./schema/post-purchase/get-upsell-pages";
import { updatePostPurchaseFlow } from "./schema/post-purchase/update-post-purchase-flow";
import { createPricingTest } from "./schema/pricing-tests/create-pricing-test";
import { endPricingTest } from "./schema/pricing-tests/end-pricing-test";
import { getPricingTestAnalytics } from "./schema/pricing-tests/get-pricing-test-analytics";
import { getPricingTests } from "./schema/pricing-tests/get-pricing-tests";
import { merchantHasPricingTests } from "./schema/pricing-tests/merchant-has-pricing-tests";
import { refreshSubscriptionPreOrderDeliveryProfile } from "./schema/refresh-subscription-pre-order-delivery-profile";
import { removeStorefrontDeliveryEstimateSelector } from "./schema/remove-storefront-delivery-estimate-selector";
import { updateCheckoutProbabilityRescueModalSettings } from "./schema/update-checkout-probability-rescue-modal-settings";
import { updateCheckoutTree } from "./schema/update-checkout-tree";
import { updateCustomJsSettings } from "./schema/update-custom-js-settings";
import { updateFulfillmentCutoffSchedule } from "./schema/update-fulfillment-cutoff-schedule";
import { updateProspectiveCheckoutTree } from "./schema/update-prospective-checkout-tree";
import { updateStorefrontDeliveryEstimateSelector } from "./schema/update-storefront-delivery-estimate-selector";
import { updateStorefrontDeliveryEstimateSettings } from "./schema/update-storefront-delivery-estimate-settings";
import { updateStorefrontProgressBarSettings } from "./schema/update-storefront-progress-bar-settings";
import { updateSubscriptionPreOrderDeliveryProfileSettings } from "./schema/update-subscription-pre-order-delivery-profile-settings";

// Performance: Split into groups to reduce TypeScript declaration emit complexity
// See: https://github.com/microsoft/TypeScript/wiki/Performance#preferring-interfaces-over-intersections

const abTestEndpoints = {
  canStartCheckoutAbTest,
  validateCheckoutTreeTransformation,
  validateCheckoutTreeTransformationForNewSplit,
  concludeCheckoutAbTest,
  createCheckoutSplitGeneration,
  createCheckoutSplitFromBuilder,
  createCheckoutSplitFromFlowSteps,
  createProspectiveCheckoutSplitFromFlowSteps,
  deleteScheduledAbTest,
  unsafeDeleteCheckoutAbTest,
  canExecuteCheckoutSplitAgentForTeam,
  executeCheckoutSplitAgent,
  getCheckoutSplitDetails,
  getCheckoutSplitListMetrics,
  getConcludedAndActiveCheckoutSplits,
  getUpcomingCheckoutSplitsInOrder,
  setSplitAnalyticsVersionOverride,
  setUpcomingCheckoutSplitsOrder,
  startScheduledCheckoutAbTest,
  getScheduledSplitTrees,
  getSplitLibrarySplits,
} as const;

const analyticsEndpoints = {
  calculateCheckoutSplitResults,
  getAllCheckoutSplitMetrics,
  getCheckoutSplitAiAnalysis,
  getCheckoutSplitSummaryMetrics,
  getCustomVisualsDataForSplit,
  getCheckoutOptimizationAnalyticsAssumptions,
  getCheckoutOptimizationRevenuePerCheckoutExperienceMetrics,
  getCheckoutOptimizationSummaryMetrics,
  getCheckoutOptimizationTimeSeriesMetrics,
  getImpactDashboardMetrics,
  getCheckoutProbabilityMeasurementMetrics,
  getMercuryAnalyticsForCheckoutExperience,
  getMercurySummaryAnalytics,
  getOnlineStoreOrderCounts,
  getOnlineStoreSessions,
  getRecentRescuePanelSplitData,
  getRescuePanelAttribution,
  getThresholdUpsellWidgetAnalytics,
  getWidgetProductAnalytics,
  getWidgetsSummaryMetrics,
  getWidgetsTimeSeriesMetrics,
  updateCheckoutOptimizationAnalyticsAssumptions,
  getCheckoutTreeAnalytics,
  getSegmentedCheckoutMetrics,
  getSegmentedCheckoutMetricsForSplit,
  getSegmentedCheckoutMetricsInsights,
  sendScheduleDemoClickedNotification,
} as const;

const billingEndpoints = {
  activateIntentRescue,
  enableIntentRescueBilling,
  generatePdfForInvoice,
  getCurrentCheckoutOptimizationInvoice,
  getFinalizedServiceInvoices,
} as const;

const checkoutExtensionEndpoints = {
  addUpsellPageToCheckoutExperience,
  constrainRateTableRelativeToOtherRateTable,
  createCheckoutExperience,
  deleteCheckoutExperience,
  duplicateCheckoutExperience,
  duplicateCheckoutWidget,
  getAllMercuryModelsForCheckoutExperience,
  getCheckoutExperienceById,
  getConfiguredServiceCodes,
  migrateCheckoutExperienceToConstraintsV2,
  getCheckoutExperienceParcelTreeFlowSteps,
  getCheckoutExperiences,
  getCheckoutExperiencesForAbTestBuilder,
  createCheckoutWidget,
  generateContentBlockWidget,
  deleteCheckoutWidget,
  getCheckoutWidgets,
  getShippingOptionWidgets,
  updateCheckoutWidget,
  assignWidgetsToExperience,
  getRateTableNameAndDescriptionById,
  removeUpsellPageFromCheckoutExperience,
  setCheckoutExperienceActive,
  setShippingMethodOrder,
  canSetCheckoutExperienceActive,
  updateCheckoutExperience,
  updateCheckoutParcelTree,
  updateMercuryModel,
} as const;

const postPurchaseEndpoints = {
  canStartPostPurchaseAbTest,
  concludePostPurchaseAbTest,
  createPostPurchaseSplitFromFlowSteps,
  deleteUpsellPage,
  duplicateUpsellPage,
  getPpuAbTestDetail,
  getPpuAbTestMetrics,
  getPpuAbTestSummaryMetrics,
  getPpuSplitAiAnalysis,
  getPostPurchaseFlowSteps,
  getPostPurchaseSplits,
  updatePostPurchaseFlow,
  getUpsellPages,
} as const;

const discountRuleEndpoints = {
  getDiscountRules,
  getDiscountRule,
  createDiscountRule,
  updateDiscountRule,
  deleteDiscountRule,
  setDiscountRuleActive,
  reorderDiscountRules,
  setUsingDiscountRules,
} as const;

const galeEndpoints = { startGaleKyc } as const;

const pricingTestEndpoints = {
  createPricingTest,
  endPricingTest,
  getPricingTestAnalytics,
  getPricingTests,
  merchantHasPricingTests,
} as const;

const otherEndpoints = {
  activateCheckoutTreeById,
  getCheckoutTreeFlowSteps,
  getCustomJsSettings,
  updateCustomJsSettings,
  getCheckoutTreeFlowStepsById,
  getCheckoutTreeFlowStepsByEventId,
  createCheckoutTree,
  getCheckoutTreeFlowStepsForSplit,
  getFulfillmentCutoffSchedule,
  getStorefrontDeliveryEstimateSettings,
  getStorefrontProgressBarSettings,
  getSubscriptionPreOrderDeliveryProfileSettings,
  addStorefrontDeliveryEstimateSelector,
  updateStorefrontDeliveryEstimateSelector,
  removeStorefrontDeliveryEstimateSelector,
  updateFulfillmentCutoffSchedule,
  updateStorefrontDeliveryEstimateSettings,
  updateStorefrontProgressBarSettings,
  updateSubscriptionPreOrderDeliveryProfileSettings,
  refreshSubscriptionPreOrderDeliveryProfile,
  getCheckoutProbabilityRescueModalSettings,
  updateCheckoutProbabilityRescueModalSettings,
  updateCheckoutTree,
  updateProspectiveCheckoutTree,
  getOmsShippingMethodNameConsiderations,
  getCheckoutTreeRecords,
} as const;

type AbTestRpc = InferRpcDefinition<typeof abTestEndpoints>;
type AnalyticsRpc = InferRpcDefinition<typeof analyticsEndpoints>;
type BillingRpc = InferRpcDefinition<typeof billingEndpoints>;
type CheckoutExtensionRpc = InferRpcDefinition<
  typeof checkoutExtensionEndpoints
>;
type PostPurchaseRpc = InferRpcDefinition<typeof postPurchaseEndpoints>;
type DiscountRuleRpc = InferRpcDefinition<typeof discountRuleEndpoints>;
type GaleRpc = InferRpcDefinition<typeof galeEndpoints>;
type PricingTestRpc = InferRpcDefinition<typeof pricingTestEndpoints>;
type OtherRpc = InferRpcDefinition<typeof otherEndpoints>;

export const rpcDefinition: typeof abTestEndpoints &
  typeof analyticsEndpoints &
  typeof billingEndpoints &
  typeof checkoutExtensionEndpoints &
  typeof discountRuleEndpoints &
  typeof galeEndpoints &
  typeof postPurchaseEndpoints &
  typeof pricingTestEndpoints &
  typeof otherEndpoints = {
  ...abTestEndpoints,
  ...analyticsEndpoints,
  ...billingEndpoints,
  ...checkoutExtensionEndpoints,
  ...discountRuleEndpoints,
  ...galeEndpoints,
  ...postPurchaseEndpoints,
  ...pricingTestEndpoints,
  ...otherEndpoints,
};

export type RedoConversionRpcDefinition = AbTestRpc &
  AnalyticsRpc &
  BillingRpc &
  CheckoutExtensionRpc &
  DiscountRuleRpc &
  GaleRpc &
  PostPurchaseRpc &
  PricingTestRpc &
  OtherRpc;
